import { useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import { ReactComponent as MergeIcon } from '@elementary/common-ui/assets/20x20/merge.svg';
import { ReactComponent as ArrowDownIcon } from '@elementary/common-ui/assets/arrow-down-expand.svg';
import {
  Avatar,
  ChipCounterList,
  DropDown,
  Flex,
  IconButton,
  Menu,
  OverflowTooltip,
  StatusChip,
  SvgIcon,
} from '@elementary/common-ui/components';
import type { ChipStatus } from '@elementary/common-ui/components/Chip/StatusChip';
import { Option } from '@elementary/common-ui/components/Menu/Menu';
import { Column, ColumnType } from '@elementary/common-ui/components/Table';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { Tooltip, Typography } from '@mui/material';
import {
  TypeChip,
  TypeChipLabel,
} from '@elementary/common-ui/components/cellRenderers/typeChip';
import { FetchTeamUsersApi } from 'shared/types';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import type { Severity, Status } from 'incidents/types';
import { severityIconMap, smallStatusIconMap } from 'incidents/utils';
import { SEVERITY_DISPLAY, STATUS_DISPLAY } from '../constants';
import { getMonitorChipStatus } from '../statusUtils';
import { IncidentListItem, IncidentListMonitorRow } from '../types';
import IncidentV2ActionsColumnMenu from './IncidentV2ActionsColumnMenu';

const MONITOR_CHIP_ORDER: ChipStatus[] = [
  'error',
  'fail',
  'runtime_error',
  'warn',
  'warning',
  'pass',
  'passed',
  'success',
  'skipped',
  'skip',
  'noData',
  'idle',
  'resolved',
];

function countMonitorsByChipStatus(
  monitors: IncidentListMonitorRow[],
): Map<ChipStatus, number> {
  const counts = new Map<ChipStatus, number>();
  for (const m of monitors) {
    const chip = getMonitorChipStatus(m.signal);
    counts.set(chip, (counts.get(chip) ?? 0) + 1);
  }
  return counts;
}

const Label = styled(Typography)`
  line-height: normal;
  white-space: nowrap;
`;

const IconButtonStyled = styled(IconButton)`
  padding: 6px;
`;

type Props = {
  severityOptions?: Option[];
  statusOptions?: Option[];
  assigneeOptions?: Option[];
  usersData?: FetchTeamUsersApi;
  disabled?: boolean;
  isAiIncidentsEnabled?: boolean;
  onOpenAIChat: (
    id: string,
    startMessage: string,
    displayLabel: string,
  ) => void;
  onChange: (
    id: string,
    val: { assignee?: string; status?: string; severity?: string },
  ) => void;
};

const useIncidentColumns = ({
  severityOptions,
  statusOptions,
  assigneeOptions,
  usersData,
  disabled,
  isAiIncidentsEnabled,
  onOpenAIChat,
  onChange,
}: Props): Column<IncidentListItem>[] =>
  useMemo<Column<IncidentListItem>[]>(
    () => [
      {
        id: 'severity',
        header: 'Severity',
        width: 100,
        sortable: true,
        filterable: false,
        renderer: (incident) => {
          const isDisabled = disabled || incident.status === 'RESOLVED';
          const displaySev =
            SEVERITY_DISPLAY[incident.severity.toLowerCase()] ?? 'Normal';
          return (
            <Menu
              tooltip="Set severity"
              menuButton={
                <IconButtonStyled disabled={isDisabled}>
                  <Flex sx={{ alignItems: 'center', gap: 1 }}>
                    {severityIconMap[displaySev as Severity]}
                    <SvgIcon width={10} strokeColor="gray4" fillColor="gray4">
                      <ArrowDownIcon />
                    </SvgIcon>
                  </Flex>
                </IconButtonStyled>
              }
              menuTitle="SET SEVERITY"
              options={severityOptions}
              closeOnChange
              onChange={(val) =>
                onChange(incident.id, {
                  severity: SEVERITY_DISPLAY[val.toLowerCase()] ?? val,
                })
              }
            />
          );
        },
      },
      {
        id: 'incident',
        header: 'Incident',
        width: 600,
        sortable: false,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => (
          <Flex sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
            <OverflowTooltip>{incident.title}</OverflowTooltip>
            {incident.monitors.length > 1 && (
              <Tooltip title="Merged — this incident contains multiple monitors">
                <TypeChip>
                  <SvgIcon width={14}>
                    <MergeIcon />
                  </SvgIcon>
                  <TypeChipLabel>Merged</TypeChipLabel>
                </TypeChip>
              </Tooltip>
            )}
          </Flex>
        ),
      },
      {
        id: 'start_time',
        header: 'First Event',
        showValueWhenEmpty: true,
        sortable: true,
        filterable: false,
        width: 130,
        type: ColumnType.Date,
        renderer: (incident) => (
          <OverflowTooltip>
            {localizeDate(incident.startTime).fromNow()}
          </OverflowTooltip>
        ),
      },
      {
        id: 'last_event',
        header: 'Last Event',
        showValueWhenEmpty: true,
        sortable: true,
        filterable: false,
        width: 130,
        type: ColumnType.Date,
        renderer: (incident) => (
          <OverflowTooltip>
            {incident.lastFailureTime
              ? localizeDate(incident.lastFailureTime).fromNow()
              : '—'}
          </OverflowTooltip>
        ),
      },
      {
        id: 'monitors',
        header: 'Monitors',
        width: 110,
        sortable: true,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const counts = countMonitorsByChipStatus(incident.monitors ?? []);
          const entries = [...counts.entries()]
            .filter(([, n]) => n > 0)
            .sort((a, b) => {
              const ia = MONITOR_CHIP_ORDER.indexOf(a[0]);
              const ib = MONITOR_CHIP_ORDER.indexOf(b[0]);
              if (ia === -1 && ib === -1) {
                return a[0].localeCompare(b[0]);
              }
              if (ia === -1) return 1;
              if (ib === -1) return -1;
              return ia - ib;
            });
          if (entries.length === 0) {
            return <Label>—</Label>;
          }
          return (
            <Flex sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {entries.map(([status, count]) => (
                <StatusChip
                  key={status}
                  status={status}
                  label={String(count)}
                  size="small"
                />
              ))}
            </Flex>
          );
        },
      },
      {
        id: 'database',
        header: 'Database',
        width: 160,
        sortable: false,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) =>
          incident.databases.length ? (
            <ChipCounterList items={incident.databases} />
          ) : (
            <Label>—</Label>
          ),
      },
      {
        id: 'schema',
        header: 'Schema',
        width: 160,
        sortable: false,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) =>
          incident.schemas.length ? (
            <ChipCounterList items={incident.schemas} />
          ) : (
            <Label>—</Label>
          ),
      },
      {
        id: 'assignee',
        header: 'Assignee',
        width: 100,
        sortable: true,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const isDisabled = disabled || incident.status === 'RESOLVED';
          const userData = usersData?.find(
            (user) => user.email === incident.assignee,
          );
          return (
            <DropDown
              value={incident.assignee ?? undefined}
              menuButton={
                <Tooltip title="Set assignee">
                  <div>
                    <IconButtonStyled disabled={isDisabled}>
                      <Flex sx={{ alignItems: 'center', gap: 1 }}>
                        <UserIcon />
                        <SvgIcon
                          width={10}
                          strokeColor="gray4"
                          fillColor="gray4"
                        >
                          <ArrowDownIcon />
                        </SvgIcon>
                      </Flex>
                    </IconButtonStyled>
                  </div>
                </Tooltip>
              }
              showClear
              showSearch
              menuSelectedButton={
                <Tooltip title={incident.assignee ?? ''}>
                  <IconButtonStyled disabled={isDisabled}>
                    <Flex sx={{ alignItems: 'center', gap: 1 }}>
                      {incident.assignee ? (
                        <Avatar
                          value={{
                            id: userData?.id || incident.assignee || '',
                            name: userData?.name || incident.assignee || '',
                          }}
                          size="small"
                        />
                      ) : (
                        <UserIcon />
                      )}
                      <SvgIcon width={10} strokeColor="gray4" fillColor="gray4">
                        <ArrowDownIcon />
                      </SvgIcon>
                    </Flex>
                  </IconButtonStyled>
                </Tooltip>
              }
              options={assigneeOptions || []}
              disabled={isDisabled}
              onChange={(val) => {
                onChange(incident.id, {
                  assignee:
                    assigneeOptions?.find((o) => o.value === val)?.value ||
                    NO_ASSIGNEE_VALUE,
                });
              }}
            />
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        width: 100,
        sortable: false,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const isDisabled = disabled || incident.status === 'RESOLVED';
          const displayStatus =
            STATUS_DISPLAY[incident.status.toLowerCase()] ?? 'Open';
          return (
            <Menu
              tooltip="Set status"
              menuButton={
                <IconButtonStyled disabled={isDisabled}>
                  <Flex sx={{ alignItems: 'center', gap: 1 }}>
                    {smallStatusIconMap[displayStatus as Status]}
                    <SvgIcon width={10} strokeColor="gray4" fillColor="gray4">
                      <ArrowDownIcon />
                    </SvgIcon>
                  </Flex>
                </IconButtonStyled>
              }
              menuTitle="SET STATUS"
              options={statusOptions}
              closeOnChange
              onChange={(val) =>
                onChange(incident.id, {
                  status: STATUS_DISPLAY[val.toLowerCase()] ?? val,
                })
              }
            />
          );
        },
      },
      {
        id: 'actions',
        header: '',
        width: 56,
        sortable: false,
        filterable: false,
        metaColumn: true,
        nonToggleable: true,
        renderer: (incident) => (
          <IncidentV2ActionsColumnMenu
            incident={incident}
            severityOptions={severityOptions}
            statusOptions={statusOptions}
            assigneeOptions={assigneeOptions}
            disabled={disabled}
            onChange={onChange}
            isAiIncidentsEnabled={isAiIncidentsEnabled}
            onOpenAIChat={onOpenAIChat}
          />
        ),
      },
    ],
    [
      severityOptions,
      statusOptions,
      assigneeOptions,
      usersData,
      disabled,
      isAiIncidentsEnabled,
      onOpenAIChat,
      onChange,
    ],
  );

export default useIncidentColumns;
